/**
 * ReglagesImagePanel — les réglages d'un calque image dans l'inspecteur :
 * flou gaussien, luminosité, contraste, niveaux, teinte et saturation.
 *
 * Les valeurs vivent dans `el.adjust` et n'altèrent jamais l'image source (cf.
 * utils/imageAdjust.js). Le panneau ne fait que trois choses : lire les valeurs
 * neutres quand un réglage est absent, écrire dans le store à chaque
 * déplacement de curseur, et poser UN point d'historique par geste.
 *
 * L'HISTORIQUE SE POSE AU RELÂCHÉ, PAS À CHAQUE PIXEL. Un curseur émet des
 * dizaines de `change` : les enregistrer tous remplirait les 50 crans de
 * l'historique avec un seul geste, et « annuler » ne reviendrait pas avant le
 * réglage mais un demi-pourcent en arrière. On écrit donc en continu pour que
 * l'aperçu suive, et on ne marque l'historique qu'à `onPointerUp` — même règle
 * que l'opacité et les ombres de l'inspecteur, où le débounce joue ce rôle.
 *
 * CHAQUE RÉGLAGE PORTE SON BOUTON DE REMISE À NEUF, et le panneau le sien pour
 * tout remettre à plat. Sans lui, retrouver « exactement 100 » à la souris sur
 * un curseur de 0 à 300 est un exercice d'adresse : c'est le défaut classique
 * d'un panneau de réglages, et il se corrige par un clic.
 */
import React from 'react';
import { useLabelStore } from '../store/useLabelStore';
import { REGLAGES_NEUTRES, reglagesDe, aDesReglages } from '../utils/imageAdjust';

// Les curseurs, dans l'ordre où on s'en sert : la tonalité d'abord, la couleur
// ensuite, le flou en dernier — c'est aussi l'ordre dans lequel ils s'appliquent.
const GROUPES = [
    {
        titre: 'Tonalité',
        champs: [
            { cle: 'brightness', nom: 'Luminosité', min: 0, max: 300, pas: 1, unite: '%' },
            { cle: 'contrast', nom: 'Contraste', min: 0, max: 300, pas: 1, unite: '%' },
        ],
    },
    {
        titre: 'Niveaux',
        aide: 'Point noir et point blanc d’entrée, puis gamma des tons moyens.',
        champs: [
            { cle: 'levelsBlack', nom: 'Point noir', min: 0, max: 254, pas: 1, unite: '' },
            { cle: 'levelsWhite', nom: 'Point blanc', min: 1, max: 255, pas: 1, unite: '' },
            { cle: 'levelsGamma', nom: 'Gamma', min: 0.1, max: 5, pas: 0.05, unite: '' },
        ],
    },
    {
        titre: 'Couleur',
        champs: [
            { cle: 'saturation', nom: 'Saturation', min: 0, max: 300, pas: 1, unite: '%' },
            { cle: 'hue', nom: 'Teinte', min: -180, max: 180, pas: 1, unite: '°' },
        ],
    },
    {
        titre: 'Flou',
        aide: 'En millimètres du document : un flou de 1 mm reste 1 mm à l’impression.',
        champs: [
            { cle: 'blur', nom: 'Flou gaussien', min: 0, max: 10, pas: 0.1, unite: ' mm' },
        ],
    },
];

/** Fond du rail : la part remplie va du neutre à la valeur, pour qu'un réglage
 *  en négatif (la teinte) se lise dans le bon sens. */
function fondRail(valeur, min, max, neutre) {
    const pc = (v) => ((v - min) / Math.max(1e-6, max - min)) * 100;
    const a = Math.min(pc(neutre), pc(valeur));
    const b = Math.max(pc(neutre), pc(valeur));
    return `linear-gradient(to right, rgba(255,255,255,0.1) ${a}%, rgba(var(--forge-accent),0.9) ${a}%, rgba(var(--forge-accent),0.9) ${b}%, rgba(255,255,255,0.1) ${b}%)`;
}

export default function ReglagesImagePanel({ el }) {
    const updateElement = useLabelStore((s) => s.updateElement);
    // OUVERT PAR DÉFAUT. Il l'était replié à la livraison, pour ne pas allonger un
    // inspecteur déjà long : l'utilisateur ne l'a pas vu du tout. Un bloc replié
    // dont l'en-tête ressemble aux libellés de section qui l'entourent n'est pas
    // « discret », il est invisible — et huit lignes de plus dans un panneau qui
    // défile coûtent moins qu'une fonctionnalité qu'on ne trouve pas.
    const [ouvert, setOuvert] = React.useState(true);

    const r = reglagesDe(el);
    const actif = aDesReglages(el);

    // Écrit un réglage sans toucher aux autres. `el.adjust` est relu dans le
    // store et non dans la copie du rendu : deux curseurs déplacés coup sur coup
    // écriraient sinon chacun par-dessus l'état d'avant l'autre.
    const poser = (cle, valeur) => {
        const courant = useLabelStore.getState().elements.find((x) => x.id === el.id);
        updateElement(el.id, { adjust: { ...reglagesDe(courant), [cle]: valeur } });
    };

    const marquer = (nom) => {
        setTimeout(() => useLabelStore.getState()._pushState(nom), 0);
    };

    const toutRemettre = () => {
        updateElement(el.id, { adjust: undefined });
        marquer('Réglages d’image');
    };

    return (
        // Un séparateur au-dessus détache la section de la grille de position qui
        // la précède : sans lui, le bloc se lisait comme la suite de ce qui était
        // au-dessus, ce qui est la moitié de la raison pour laquelle il passait
        // inaperçu. L'autre moitié était l'en-tête, traité ci-dessous.
        <div className="mt-5 pt-4 border-t border-white/10">
            {/* UN EN-TÊTE QUI SE VOIT. Avant : un libellé mono gris à 9 px, comme
                les dix étiquettes de champ qui l'entourent, précédé d'un chevron de
                9 px — rien ne le distinguait d'un intitulé de plus, et rien ne
                disait qu'on pouvait cliquer. Il est maintenant posé sur une barre
                cliquable pleine largeur, avec une icône de réglages et un titre en
                blanc appuyé. Pas d'Encre dorée en fond : la Règle de l'étincelle
                réserve l'or à l'ACTION, et déplier une section n'en est pas une —
                seule la pastille d'état l'emploie, pour signaler un réglage posé. */}
            <button
                onClick={() => setOuvert((v) => !v)}
                title={ouvert ? 'Replier les réglages d’image' : 'Déplier les réglages d’image'}
                aria-expanded={ouvert}
                className="w-full flex items-center gap-2 px-2.5 py-2 mb-2 rounded-lg bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 hover:border-white/20 transition-all"
            >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round" className="text-white/70 shrink-0">
                    {/* Curseurs de réglage — le pictogramme universel de ce panneau. */}
                    <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" />
                    <line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" />
                    <line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" />
                    <line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" />
                    <line x1="17" y1="16" x2="23" y2="16" />
                </svg>
                <span className="flex-1 text-left text-[10px] font-mono font-bold uppercase tracking-widest text-white/85">
                    Réglages d’image {el.locked && '(Verrouillée)'}
                </span>
                {/* Une pastille dit qu'un réglage est posé même panneau replié :
                    sinon une image assombrie sans raison visible envoie chercher
                    le défaut du côté du fichier source. */}
                {actif && <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: 'rgba(var(--forge-accent),1)' }} />}
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"
                    strokeLinecap="round" strokeLinejoin="round" className="text-white/50 shrink-0"
                    style={{ transform: ouvert ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>
                    <polyline points="9 18 15 12 9 6" />
                </svg>
            </button>

            {ouvert && (
                <div className={`space-y-4 p-3 bg-black/20 rounded-xl border border-white/5 ${el.locked ? 'opacity-50 pointer-events-none' : ''}`}>
                    {/* « Réinitialiser » vit DANS le bloc depuis que l'en-tête est une
                        barre cliquable : deux boutons imbriqués n'existent pas en HTML,
                        et un bouton posé par-dessus la barre aurait rendu une partie de
                        celle-ci muette. Absent sur un calque verrouillé — le conteneur
                        est neutralisé, mais un bouton laissé dans l'en-tête aurait été
                        le seul moyen d'effacer les réglages d'un calque qu'on venait
                        justement de verrouiller. */}
                    {actif && !el.locked && (
                        <div className="flex justify-end -mb-1">
                            <button
                                onClick={toutRemettre}
                                title="Remettre tous les réglages à neutre"
                                className="text-[9px] font-mono uppercase tracking-widest text-white/50 hover:text-white transition-colors"
                            >
                                Réinitialiser
                            </button>
                        </div>
                    )}
                    {GROUPES.map((g) => (
                        <div key={g.titre}>
                            {/* 9 px MAJUSCULES tracking-widest, plancher d'opacité
                                white/50 : la Règle de l'étiquette mono de DESIGN.md,
                                dont le plancher vient d'un audit de contraste. */}
                            <div className="text-[9px] font-mono uppercase tracking-widest text-white/50 mb-2">{g.titre}</div>
                            {g.champs.map((c) => {
                                const neutre = REGLAGES_NEUTRES[c.cle];
                                const val = r[c.cle];
                                const modifie = Math.abs(val - neutre) > 1e-6;
                                return (
                                    <div key={c.cle} className="mb-2.5">
                                        <label className="flex justify-between items-center text-[9px] text-white/50 uppercase tracking-widest font-mono mb-1">
                                            <span>{c.nom}</span>
                                            <span className="flex items-center gap-1.5">
                                                <span className={modifie ? 'text-white/90 font-bold' : 'text-white/50'}>
                                                    {c.pas < 1 ? val.toFixed(c.pas < 0.1 ? 2 : 1) : Math.round(val)}{c.unite}
                                                </span>
                                                {modifie && (
                                                    <button
                                                        onClick={() => { poser(c.cle, neutre); marquer(c.nom); }}
                                                        title={`Remettre ${c.nom.toLowerCase()} à ${neutre}${c.unite}`}
                                                        aria-label={`Remettre ${c.nom.toLowerCase()} à sa valeur neutre`}
                                                        className="text-white/50 hover:text-white transition-colors"
                                                    >
                                                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                                            <path d="M3 12a9 9 0 1 0 3-6.7" /><polyline points="3 4 3 10 9 10" />
                                                        </svg>
                                                    </button>
                                                )}
                                            </span>
                                        </label>
                                        <input
                                            type="range"
                                            min={c.min}
                                            max={c.max}
                                            step={c.pas}
                                            value={val}
                                            onChange={(e) => poser(c.cle, Number(e.target.value))}
                                            onPointerUp={() => marquer(c.nom)}
                                            onKeyUp={() => marquer(c.nom)}
                                            onDoubleClick={() => { poser(c.cle, neutre); marquer(c.nom); }}
                                            title={`${c.nom} — double-clic pour revenir au neutre`}
                                            className="w-full h-1 rounded-lg appearance-none cursor-pointer outline-none shadow-inner"
                                            style={{ background: fondRail(val, c.min, c.max, neutre) }}
                                        />
                                    </div>
                                );
                            })}
                            {g.aide && <div className="text-[9px] font-mono text-white/50 leading-snug">{g.aide}</div>}
                        </div>
                    ))}

                    {/* Une seule phrase, celle qu'on ne devine pas : le reste — que
                        l'image d'origine est intacte — se lit dans le fait que tout
                        revient au neutre d'un clic. Le manuel d'aide détaille. */}
                    <div className="text-[9px] font-mono text-white/50 leading-snug border-t border-white/5 pt-2.5">
                        Non destructif, et cuit dans les pixels à l’export — PDF, PNG et SVG.
                    </div>
                </div>
            )}
        </div>
    );
}
